<script lang="ts" setup>
import AdaptiveDialog from '../AdaptiveDialog.vue'
import SrInputNumber from '@/components/SrInputNumber.vue'

import { Tabs, TabList, Tab, TabPanel, TabPanels } from "primevue"
import Message from 'primevue/message'
import Skeleton from 'primevue/skeleton'
import IftaLabel from 'primevue/iftalabel'
import { watch, computed, ref, useTemplateRef, onMounted, onUnmounted } from 'vue'
import { FormField, Form } from '@primevue/forms'
import { zodResolver } from '@primevue/forms/resolvers/zod'
import { z } from 'zod'
import isNumber from 'lodash/isNumber'
import type { CcxtMarket, OrderSide } from '@/services/types'
import type { FormInstance } from '@primevue/forms'
import { useCreateSwapOrderSettings } from '@/composable/settings/useCreateSwapOrderSettings'
import {
  preventEnter,
  numberWithCommas,
} from '@/utils'
import CexDexCexLegForm from './cex-dex/CexDexCexLegForm.vue'
import CexDexDexLegForm from './cex-dex/CexDexDexLegForm.vue'
import CexDexTwoLegsForm from './cex-dex/CexDexTwoLegsForm.vue'
import { useProfiles } from '@/composable/useMultiProfiles'
import { useCexFeatures } from '@/composable/useCexFeatures'
import { i18n } from '@/i18n';
import { useCexCexTwoLegsSettings } from '@/composable/settings/useCexCexTwoLegsSettings'
import ProfilesFieldset from '../ProfilesFieldset.vue'
import { useMyToast } from '@/composable/useMyToast'
import { useTriggerSettings } from '@/composable/settings/useTriggerSettings'
import { useCexLimitsRefresher } from '@/composable/refreshers/useCexLimitsRefresher'
import { useCexPositionsSide } from '@/composable/useCexPositionsSide'
import { useDexSwapForm } from '@/composable/useDexSwapForm'
import type { MarketType } from '@/utils/types'
import { useAverageSpreadSettings } from '@/composable/settings/useAverageSpreadSettings'
import { useTrailingSlSettings } from '@/composable/settings/useTrailingSlSettings'
import { isNull } from 'lodash'
import type { Tick } from '@/utils/ticks'
import { useRoute } from 'vue-router'
import { useSound } from '@vueuse/sound'
import { useFlasherStore } from '@/stores/flasherStore'
import bellSoundFile from '@/assets/sounds/alert.wav'
// import { useCheckUserRights } from '@/composable/useCheckUserRights'

type AVAILABLE_MODES = "cex" | "dex" | "cex-dex"
const choosenMode = ref<AVAILABLE_MODES>("cex")
const SLIPPAGE_MAX = 10
const SLIPPAGE_MIN = -10
const initValues = {
  amount: null,
  leverage: null,
  slippage: null,
}

const emit = defineEmits(["show-positions-modal", "update-user-positions"])
const props = defineProps<{
  dexVol: number | null
  recommendedAmount?: number | null
  spread?: number | null
  backSpread?: number | null
  cex: string
  asset: string
  chain: string,
  market: CcxtMarket
  priceBuy: number | null
  contract: string
  dexTicks: Tick[]
  cexTicks: Tick[]
  // zscoreDex: number | null | undefined
  dexMovementSize: number | null | undefined
  priceDex: number | null
  priceCex: number | null
  priceSell: number | null
  dexPriceBuy: number | null
  dexPriceSell: number | null
  cacheKey: string
  quoteBuy?: string
}>()

const settingsForm = useTemplateRef<FormInstance>('settingsForm')
const cex = computed(() => props.cex)
const asset = computed(() => props.asset)
const marketType = computed(() => "swap" as MarketType)
// const cacheKeyPostfix = computed(() => `${props.cex}/${props.asset}`.toLocaleLowerCase())
const cacheKeyPostfix = computed(() => props.cacheKey)
// const { isDexCexAdvanced } = useCheckUserRights()
// const isDexCexAdvanced = ref(false) // TODO: Заглушка
const marketMinLimit = computed(() => 10 ** -Number(props.market.precision.amount ?? 1))
const visible = ref(false)
const amountFractionDigits = ref(8)
const approxOrderCost = ref<string>('')
const minimized = ref(false)
const { profiles, profilesMax, enabledProfiles, toggleProfile } = useProfiles(cacheKeyPostfix, "dex_cex")
const { positionsSide } = useCexPositionsSide(cex, asset, marketType, enabledProfiles, props.quoteBuy || 'USDT')
const { avgSpread } = useAverageSpreadSettings(cacheKeyPostfix)
const { trailingSl, trailingSide, initTrailingStopLoss, cleanTrailingSl } = useTrailingSlSettings(cacheKeyPostfix)
const { limitsRefresher, updateLimits, limits } = useCexLimitsRefresher(cex, asset, marketType, props.quoteBuy || 'USDT')
const { t } = i18n.global
const { autoPrepare, orderNotifications } = useCexCexTwoLegsSettings()
const { isProfilesPrepared, preparingProfile, creatingOrder, fetchingBalance, prepareProfileWrapper, fetchBalanceWrapper, createOrderWrapper, createReduceOnlyOrderWrapper } = useCexFeatures(orderNotifications, enabledProfiles, profilesMax, "dex_cex")
const { addToast } = useMyToast()

const bellSound = useSound(bellSoundFile)
const flasherStore = useFlasherStore()

const {
  initSettingsForm,
  leverage,
  amount,
  slippage,
  jumpRequired
} = useCreateSwapOrderSettings(settingsForm, cacheKeyPostfix, "dex")

const toggle = () => {
  visible.value = !visible.value
}

const onAmountChange = (v: number | null | undefined) => {
  if (!props.priceBuy || !props.priceSell) {
    return
  }
  if (!v || !isNumber(v)) {
    approxOrderCost.value = ''
  } else {
    const average = (props.priceBuy + props.priceSell) / 2
    approxOrderCost.value = `(≈${Number((v * average) / (leverage.value ?? 1)).toFixed(2)} USDT)`
  }
}

const resolver = zodResolver(
  z.object({
    amount: z.coerce
      .number({ error: 'numberError' })
      .gt(0, { message: 'modal.createSwapOrders.errors.amountNullError' }),
  }),
)

// Вотчеры
watch(leverage, () => {
  onAmountChange(amount.value)
  isProfilesPrepared.value = false
})

// const onFormSubmit = (e: FormSubmitEvent) => {
//   if (!e.valid) {
//     return
//   }
// }

const onShow = async () => {
  // Подгружаем из кэша данные формы
  initSettingsForm(props.recommendedAmount ?? null)
  onAmountChange(Number(amount.value))
}

const onHide = async () => {
}

defineExpose({ toggle })

// Переменные для компонентов форм
const formMarketData = computed(() => {
  return {
    "cex": props.cex,
    "asset": props.asset,
    "cexPrice": props.priceCex,
    "dexPrice": props.priceDex,
    "cexPriceBuy": props.priceBuy,
    "cexPriceSell": props.priceSell,
    "spread": props.spread ?? null,
    "backSpread": props.backSpread ?? null,
  }
})

const cexLegSpecificData = computed(() => {
  return {
    "isProfilesPrepared": isProfilesPrepared.value,
    "preparingProfile": preparingProfile.value,
    "creatingOrder": creatingOrder.value,
    "fetchingBalance": fetchingBalance.value,
    "enabledProfiles": enabledProfiles.value,
    "amount": amount.value,
    "leverage": leverage.value,
    "jumpRequired": jumpRequired.value,
  }
})

const twoLegsLegSpecificData = computed(() => ({
  "isProfilesPrepared": isProfilesPrepared.value,
  "preparingProfile": preparingProfile.value,
  "creatingOrder": creatingOrder.value,
  "fetchingBalance": fetchingBalance.value,
  "enabledProfiles": enabledProfiles.value,
  "canClosePositions": positionsSide.value === 'short'
    && dexSwapForm.tokenBalance.value !== null
    && dexSwapForm.tokenBalance.value > 0,
}))

const cexTrigger = useTriggerSettings(cacheKeyPostfix, "cex")
const dexTrigger = useTriggerSettings(cacheKeyPostfix, "dex")
const twoLegsTrigger = useTriggerSettings(cacheKeyPostfix, "two-legs")
const twoLegsDirection = ref<'long' | 'short' | null>(null)

const dexSwapForm = useDexSwapForm({
  chain: computed(() => props.chain),
  contract: computed(() => props.contract),
  asset: computed(() => props.asset),
  amount: computed(() => amount.value ?? null),
  dexPriceBuy: computed(() => props.dexPriceBuy),
  dexPriceSell: computed(() => props.dexPriceSell),
  slippage: computed(() => slippage.value ?? null),
  market: computed(() => props.market),
})

const dexLegSpecificData = computed(() => ({
  swapping: dexSwapForm.swapping.value,
  usdtBalance: dexSwapForm.usdtBalance.value,
  tokenBalance: dexSwapForm.tokenBalance.value,
  balancesLoading: dexSwapForm.balancesLoading.value,
}))

onMounted(() => {
  updateLimits()
  limitsRefresher.startRefresh()
  emit('update-user-positions')
})

onUnmounted(() => {
  limitsRefresher.stopRefresh()
})

// Методы для компонентов форм
const prepareProfileWargs = async () => {
  return await prepareProfileWrapper(props.cex as string, props.asset as string, leverage.value as number, props.quoteBuy)
}
const fetchBalanceWargs = async () => {
  return await fetchBalanceWrapper(props.cex as string)
}

const updateOpenPositionsUntilSuccess = () => {
  emit('update-user-positions')
  if (positionsSide.value === null) {
    window.setTimeout(() => {
      updateOpenPositionsUntilSuccess()
    }, 1500)
  }
}

const createCexOrderWargs = async (side: "buy" | "sell") => {
  console.log(`createCexOrderWargs called`)
  addToast({
    message: t(
      side == 'buy'
        ? 'modal.createSwapOrders.placingBuyOrder'
        : 'modal.createSwapOrders.placingSellOrder',
    ),
    severity: 'success',
  })

  cexTrigger.triggerValue.value = null
  if (autoPrepare.value && !isProfilesPrepared.value) {
    await prepareProfileWargs()
  }

  let orderPrice = side == "buy" ? props.priceBuy : props.priceSell
  if (!orderPrice) {
    throw Error("orderPrice is null")
  }

  const slippagePercent = (slippage.value ?? 0) / 100
  if (side == 'buy') {
    orderPrice += orderPrice * slippagePercent
  } else {
    orderPrice -= orderPrice * slippagePercent
  }

  let amountMaxByCost = limits?.value?.cost_max ? Math.max(1, limits?.value?.cost_max - 5) / orderPrice : null
  if (amountMaxByCost) {
    const ctrSize = props.market.contractSize
    if (ctrSize) {
      amountMaxByCost = Math.round(amountMaxByCost / ctrSize) * ctrSize
    }
  }
  const amountMax = limits?.value?.amount_max ?? null
  let amountMaxF = null
  if (amountMaxByCost && amountMax) {
    amountMaxF = Math.min(amountMaxByCost, amountMax)
  } else if (amountMaxByCost && !amountMax) {
    amountMaxF = amountMaxByCost
  } else if (!amountMaxByCost && amountMax) {
    amountMaxF = amountMax
  }

  let finalOrderAmount = amount.value
  if (amountMaxF && finalOrderAmount) {
    finalOrderAmount = Math.min(amountMaxF, finalOrderAmount)
  }

  if (!props.cex || !props.asset || !amount.value) {
    throw Error("exBuy or asset or amount is null")
  }

  await createOrderWrapper(
    props.cex,
    props.asset,
    side,
    amount.value,
    orderPrice,
    leverage.value ?? 1,
    false,
    props.quoteBuy,
  )
  updateOpenPositionsUntilSuccess()
}

const updateClosedPositionsUntilSuccess = () => {
  emit('update-user-positions')
  if (positionsSide.value !== null) {
    window.setTimeout(() => {
      updateOpenPositionsUntilSuccess()
    }, 1500)
  }
}

const closeCexPosition = async () => {
  if (!props.cex || !props.asset || !props.priceBuy || !props.priceSell) {
    return
  }
  await createReduceOnlyOrderWrapper(props.cex, props.asset, props.priceBuy, props.priceSell, props.quoteBuy)
  cleanTrailingSl()
  updateClosedPositionsUntilSuccess()
}

const route = useRoute()
const isDebug = computed(() => route.query.debug !== undefined)

const openCexPosition = async (initTsl: boolean = false) => {
  const avg = avgSpread.value ?? 0
  const longSpread = Number(props.spread) - avg
  const shortSpread = Number(props.backSpread) + avg
  let isLong = false

  if (isDebug.value) {
    isLong = longSpread > shortSpread
  } else {
    if (longSpread > 0.25) {
      isLong = true
    } else if (shortSpread > 0.25) {
      isLong = false
    } else {
      addToast({
        message: t('modal.createSwapOrders.noSpread'),
        severity: 'error',
      })
      return
    }
  }

  await createCexOrderWargs(isLong ? 'buy' : 'sell')
  if (initTsl && trailingSl.value && props.priceDex && props.spread && props.backSpread) {
    initTrailingStopLoss(isLong, props.priceDex, avgSpread.value, props.spread, props.backSpread)
  }
}

const handleOpenTwoLegsShort = async () => {
  const avg = avgSpread.value ?? 0
  const shortSpread = Number(props.backSpread) + avg

  if (!shortSpread || shortSpread <= 0) {
    addToast({
      message: t('modal.createSwapOrders.noSpread'),
      severity: 'error',
    })
    return
  }

  twoLegsTrigger.triggerValue.value = null
  if (autoPrepare.value && !isProfilesPrepared.value) {
    await prepareProfileWargs()
  }

  // Step 1: DEX buy
  const dexResult = await dexSwapForm.swapDex('buy')
  if (!dexResult.success) {
    return // toast already shown by composable
  }

  // Step 2: CEX sell (short)
  await createCexOrderWargs('sell')

  twoLegsDirection.value = 'short'

  if (trailingSl.value) {
    if (props.priceDex && props.spread && props.backSpread) {
      initTrailingStopLoss(false, props.priceDex, avgSpread.value, props.spread, props.backSpread)
    }
  }
}

const handleCloseTwoLegsShort = async () => {
  await closeCexPosition()
  await dexSwapForm.swapDex('sell')
  twoLegsDirection.value = null
}

const handleSwapDexBuy = async () => {
  const result = await dexSwapForm.swapDex('buy')
  if (result.success && trailingSl.value && props.priceCex && props.spread && props.backSpread) {
    initTrailingStopLoss(true, props.priceCex, avgSpread.value, props.spread, props.backSpread)
  }
}

const handleSwapDexSell = async () => {
  const result = await dexSwapForm.swapDex('sell')
  if (result.success && trailingSl.value && props.priceCex && props.spread && props.backSpread) {
    initTrailingStopLoss(false, props.priceCex, avgSpread.value, props.spread, props.backSpread)
  }
}

const handleCloseDexPosition = async () => {
  if (trailingSide.value === 'long') {
    await dexSwapForm.swapDex('sell')
  } else if (trailingSide.value === 'short') {
    await dexSwapForm.swapDex('buy')
  }
  cleanTrailingSl()
}

const handleTwoLegsSpreadWatch = () => {
  const trigger = twoLegsTrigger.triggerValue.value
  if (isNull(trigger) || isNull(props.spread) || isNull(props.backSpread)) {
    return
  }

  if (creatingOrder.value) {
    return
  }

  const avg = avgSpread.value ?? 0
  const shortSpread = Number(props.backSpread) + avg

  // По двум ногам мы можем открываться на CEX только в шорт
  if (shortSpread >= trigger) {
    if (jumpRequired.value) {
      const latestTicks = props.dexTicks?.slice(-5).map(x => x[1]) ?? []
      const latestRightTickPrice = latestTicks?.[latestTicks.length - 1] ?? null
      if (latestRightTickPrice) {
        const maxRightPrice = Math.max(...latestTicks)
        const shortSize = -(latestRightTickPrice / maxRightPrice - 1) * 100
        if (shortSize < trigger * 0.8) {
          return
        }
      }
    }
    bellSound.play()
    flasherStore.start()
    handleOpenTwoLegsShort()
  }
}

const onCexSpreadTriggered = async (side: OrderSide) => {
  cexTrigger.triggerValue.value = null
  bellSound.play()
  flasherStore.start()
  await createCexOrderWargs(side)
  if (trailingSl.value && props.priceDex && props.spread && props.backSpread) {
    initTrailingStopLoss(side == "buy", props.priceDex, avgSpread.value, props.spread, props.backSpread)
  }
}

const onDexSpreadTriggered = async (side: OrderSide) => {
  dexTrigger.triggerValue.value = null
  bellSound.play()
  flasherStore.start()
  // alert(`dex открытие ${side}`)
  await dexSwapForm.swapDex(side)
  // await createCexOrderWargs(side)
  // if (trailingSl.value && props.priceDex && props.spread && props.backSpread) {
  //   initTrailingStopLoss(side == "buy", props.priceDex, avgSpread.value, props.spread, props.backSpread)
  // }
}

const handleCexSpreadWatch = (
  v: number,
  side: "buy" | "sell",
) => {

  const trigger = cexTrigger.triggerValue.value
  if (isNull(trigger) || isNull(v)) {
    return
  }

  if (v >= trigger) {
    if (jumpRequired.value) {
      const latestTicks = props.dexTicks?.slice(-5).map(x => x[1]) ?? []
      const isLong = side == "buy"
      const latestRightTickPrice = latestTicks?.[latestTicks.length - 1] ?? null
      if (latestRightTickPrice) {
        const minRightPrice = Math.min(...latestTicks)
        const maxRightPrice = Math.max(...latestTicks)
        const longSize = (latestRightTickPrice / minRightPrice - 1) * 100
        const shortSize = -(latestRightTickPrice / maxRightPrice - 1) * 100
        if (isLong && longSize < trigger * 0.8 || !isLong && shortSize < trigger * 0.8) {
          return
        }
      }
    }
    onCexSpreadTriggered(side)
  }
}

const handleDexSpreadWatch = (
  v: number,
  side: "buy" | "sell",
) => {

  const trigger = dexTrigger.triggerValue.value
  if (isNull(trigger) || isNull(v)) {
    return
  }

  if (v >= trigger) {
    if (jumpRequired.value) {
      const latestTicks = props.cexTicks?.slice(-5).map(x => x[1]) ?? []
      const isLong = side == "buy"
      const latestRightTickPrice = latestTicks?.[latestTicks.length - 1] ?? null
      if (latestRightTickPrice) {
        const minRightPrice = Math.min(...latestTicks)
        const maxRightPrice = Math.max(...latestTicks)
        const longSize = (latestRightTickPrice / minRightPrice - 1) * 100
        const shortSize = -(latestRightTickPrice / maxRightPrice - 1) * 100
        if (isLong && longSize < trigger * 0.8 || !isLong && shortSize < trigger * 0.8) {
          return
        }
      }
    }
    onDexSpreadTriggered(side)
  }
}

// Следим за спредами для триггера
watch(() => props.spread, (v) => {
  if (v && choosenMode.value === "cex") handleCexSpreadWatch(v, "buy")
})
watch(() => props.backSpread, (v) => {
  if (v && choosenMode.value === "cex") handleCexSpreadWatch(v, "sell")
  else if (v && choosenMode.value === "dex") handleDexSpreadWatch(v, "buy")
})

watch(() => props.spread, (v) => {
  if (v && choosenMode.value === "cex-dex") handleTwoLegsSpreadWatch()
})
watch(() => props.backSpread, (v) => {
  if (v && choosenMode.value === "cex-dex") handleTwoLegsSpreadWatch()
})

</script>

<template>
  <AdaptiveDialog @show="onShow" @hide="onHide" v-model:visible="visible" class="md:w-170 w-full"
    :class="{ 'w-100': minimized, 'lg:w-220 xl:w-250': !minimized }" pt:footer:style="padding: 0px">
    <template #header>
      <div class="flex items-center">
        <!-- <Button @click="minimized = !minimized" :icon="minimized ? 'pi pi-window-maximize' : 'pi pi-window-minimize'"
          rounded variant="text"></Button> -->
        <span class="ms-2">
          {{ $t('modal.createSwapOrders.openTitle', {
            spread: props.spread ? Number(props.spread).toFixed(2) : '-',
            backSpread: props.backSpread ? Number(props.backSpread).toFixed(2) : '-',
          }) }}
        </span>
      </div>
    </template>
    <div v-show="!minimized">
      <!-- Спред в лонг и спред в шорт -->
      <div class="grid grid-cols-2 px-1 py-2 border-color border-b-1">
        <div class="text-start">{{ $t('longSpread') }}</div>
        <div class="flex justify-end items-center">
          <span v-if="props.spread">{{ Number(props.spread).toFixed(2) }}%</span>
          <Skeleton width="40px" v-else></Skeleton>
        </div>
      </div>
      <div class="grid grid-cols-2 px-1 py-2">
        <div class="text-start">{{ $t('shortSpread') }}</div>
        <div class="flex justify-end items-center">
          <span v-if="props.backSpread">{{ Number(props.backSpread).toFixed(2) }}%</span>
          <Skeleton width="40px" v-else></Skeleton>
        </div>
      </div>
      <!-- Цена в лонг и цена в шорт -->
      <div class="grid grid-cols-2 px-1 py-2 border-color border-b-1 border-t-1">
        <div class="text-start">{{ $t('longPrice') }}</div>
        <div class="flex justify-end items-center">
          <span v-if="props.priceBuy">{{ props.priceBuy }}</span>
          <Skeleton width="40px" v-else></Skeleton>
        </div>
      </div>
      <div class="grid grid-cols-2 px-1 py-2">
        <div class="text-start">{{ $t('shortPrice') }}</div>
        <div class="flex justify-end items-center">
          <span v-if="props.priceSell">{{ props.priceSell }}</span>
          <Skeleton width="40px" v-else></Skeleton>
        </div>
      </div>

      <!-- Данные формы -->
      <Form ref="settingsForm" class="grid gap-1 w-full mt-4 grid-cols-1 md:grid-cols-3" :resolver="resolver"
        :initial-values="initValues" id="orders-form">
        <!-- Объём -->
        <FormField v-slot="$field" class="relative" name="amount">
          <IftaLabel>
            <SrInputNumber allow-empty @change="onAmountChange" @keydown="preventEnter" type="text"
              :maxFractionDigits="amountFractionDigits" placeholder="1000" :min="marketMinLimit" fluid />
            <label>{{ $t('modal.createSwapOrders.amount') }}{{ approxOrderCost }}</label>
          </IftaLabel>
          <Message v-if="$field?.invalid" class="absolute" severity="error" size="small" variant="simple">{{
            $t($field.error?.message) }}
          </Message>
        </FormField>

        <!-- Плечо -->
        <FormField v-slot="$field" class="relative" name="leverage">
          <IftaLabel>
            <SrInputNumber showButtons :min="1" @keydown="preventEnter" :max="10" :step="1" :maxFractionDigits="0"
              placeholder="1" fluid />
            <label>{{ $t('modal.createSwapOrders.leverage') }}</label>
          </IftaLabel>
          <Message v-if="$field?.invalid" class="absolute" severity="error" size="small" variant="simple">{{
            $t($field.error?.message, { min: 1, max: limits?.leverage_max }) }}
          </Message>
        </FormField>

        <!-- Проскальзывание -->
        <FormField v-slot="$field" class="relative" name="slippage">
          <IftaLabel>
            <SrInputNumber showButtons :step="0.1" @keydown="preventEnter" :maxFractionDigits="2" placeholder="3"
              fluid />
            <label>{{ $t('modal.createSwapOrders.slippage') }}</label>
          </IftaLabel>
          <Message v-if="$field?.invalid" class="absolute" severity="error" size="small" variant="simple">{{
            $t($field.error?.message, { min: SLIPPAGE_MIN, max: SLIPPAGE_MAX }) }}
          </Message>
        </FormField>
      </Form>

      <!-- Лимиты -->
      <div class="grid grid-cols-2 px-1 py-2 py-2 border-b-1 border-t-1 border-color mt-6">
        <div class="flex justify-start items-center">
          <div class="text-start">{{ $t('modal.createSwapOrders.limitQuote') }}</div>
        </div>
        <div class="flex justify-end items-center">
          <div class="text-start">
            {{ limits?.amount_max ? numberWithCommas(limits?.amount_max) : '—' }}
          </div>
        </div>
      </div>
      <div class="grid grid-cols-2 px-1 py-2 py-2 border-b-1 border-color">
        <div class="flex justify-start items-center">
          <div class="text-start">{{ $t('modal.createSwapOrders.limitUsdt') }}</div>
        </div>
        <div class="flex justify-end items-center">
          <div class="text-start">
            {{ limits?.cost_max ? numberWithCommas(limits?.cost_max) : '—' }}
          </div>
        </div>
      </div>
      <div class="grid grid-cols-2 px-1 py-2 py-2 border-b-1 border-color mb-0">
        <div class="flex justify-start items-center">
          <div class="text-start">{{ $t('modal.createSwapOrders.limitLeverage') }}</div>
        </div>
        <div class="flex justify-end items-center">
          <div class="text-start">
            {{ limits?.leverage_max ? numberWithCommas(limits?.leverage_max) : '—' }}
          </div>
        </div>
      </div>
    </div>
    <template #footer>
      <div class="w-full">
        <Tabs class="w-full" value="cex" @update:value="(v) => { choosenMode = v as AVAILABLE_MODES }">
          <TabList>
            <Tab value="cex" pt:root:class="text-sm px-4 py-2">CEX</Tab>
            <Tab value="dex" pt:root:class="text-sm px-4 py-2">DEX</Tab>
            <Tab value="cex-dex" pt:root:class="text-sm px-4 py-2">CEX / DEX</Tab>
          </TabList>
          <TabPanels>
            <TabPanel value="cex">
              <div class="p-4">
                <!-- Работа в одну ногу, CEX -->
                <CexDexCexLegForm :market-data="formMarketData" :form-data="cexLegSpecificData"
                  :settings-form="settingsForm" :cache-key-postfix="cacheKeyPostfix"
                  @prepare-profile="prepareProfileWargs" :positions-side="positionsSide"
                  @fetch-balance="fetchBalanceWargs" @close-cex-position="closeCexPosition"
                  @show-positions-modal="emit('show-positions-modal')" @open-cex-position="openCexPosition"
                  :minimized="minimized">
                </CexDexCexLegForm>
              </div>
            </TabPanel>
            <TabPanel value="dex">
              <div class="p-4">
                <!-- Работа в одну ногу, DEX -->
                <CexDexDexLegForm :cache-key-postfix="cacheKeyPostfix" :amount="amount ?? 0" :chain="props.chain"
                  :contract="props.contract" :asset="props.asset" :slippage="slippage ?? null"
                  :dex-price-buy="props.dexPriceBuy" :dex-price-sell="props.dexPriceSell" :price-cex="props.priceCex"
                  :spread="props.spread" :back-spread="props.backSpread" :market="props.market"
                  :form-data="dexLegSpecificData" @swap-dex-buy="handleSwapDexBuy" @swap-dex-sell="handleSwapDexSell"
                  @close-dex-position="handleCloseDexPosition">
                </CexDexDexLegForm>
              </div>
            </TabPanel>
            <TabPanel value="cex-dex">
              <div class="p-4">
                <CexDexTwoLegsForm :market-data="formMarketData" :form-data="twoLegsLegSpecificData"
                  :cache-key-postfix="cacheKeyPostfix" :minimized="minimized" :amount="amount ?? null"
                  :leverage="leverage ?? null" :slippage="slippage ?? null" :chain="props.chain"
                  :contract="props.contract" :dex-price-buy="props.dexPriceBuy" :dex-price-sell="props.dexPriceSell"
                  :market="props.market" @open-two-legs-short="handleOpenTwoLegsShort"
                  @close-two-legs-short="handleCloseTwoLegsShort" @prepare-profile="prepareProfileWargs"
                  @fetch-balance="fetchBalanceWargs" @show-positions-modal="emit('show-positions-modal')" />
              </div>
            </TabPanel>
          </TabPanels>
        </Tabs>
        <ProfilesFieldset v-show="!minimized" class="m-2 mt-0" :toggle-profile="toggleProfile" :profiles="profiles"
          :enabled-profiles="enabledProfiles">
        </ProfilesFieldset>
      </div>
    </template>
  </AdaptiveDialog>
</template>
